import { createSelector } from 'reselect';
import { flow } from 'fp-ts/function';
import { array, record, semigroup, string } from 'fp-ts';
import { compose, get } from 'lodash/fp';
import { compactArray, getStringArrayProp, getStringProp } from '@mews/fp';
import { composeProps, createPropsExtender } from '@mews/framework';
import { Reservation } from '@mews-commander/data-access';
import { isCanceledState } from '@mews-commander/types';
import { getDurationAsMilliseconds } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { getAccommodationEnvironment } from '@mews-commander/core/legacy-globalization';
import {
    getReservations,
    getReservationsByGroupId,
    getReservation,
    getReservationGroupIdsByAvailabilityBlockId,
    getReservationsByIds,
    haveReservationsCommonBooker,
    getReservationsCustomerIds,
    getGroupLockableReservations,
    getReservationsByAvailabilityBlockId,
} from '../ducks/reservations';
import { getSortedSpaces } from '../ducks/spaces';
import { EnhancedResource } from '../ducks/resourcesTree';
import { getReservationLatestUndoCancelationUtc, getReservationPersonCount } from '../utils/reservations';
import { getEnterpriseTimeZoneIdentifier } from '../ducks/enterprises';
import { getServices } from '../ducks/services';
import {
    getCurrentEnterpriseEditableHistoryIntervalMilliseconds,
    getCurrentEnterpriseOperationalEditableHistoryWindow,
    getCurrentEnterpriseAccommodationEnvironmentCode,
} from './session/enterprise';

interface ReservationsSortedResourcesParam {
    reservations: Array<Reservation>;
    sortedResources: Array<EnhancedResource>;
}

const isEmptyOrSizeOneOfSpacesOrReservations = ({ reservations, sortedResources }: ReservationsSortedResourcesParam) =>
    reservations == null || sortedResources == null || sortedResources.length < 2 || reservations?.length < 2;

const createMapFromIndexEntries = record.fromFoldable(semigroup.first<number>(), array.Foldable);
const createIdIndexMap = (ids: string[]) => createMapFromIndexEntries(ids.map((id, index) => [id, index]));

const createIdsIndexOf = (ids: string[]) => {
    const indexMap = createIdIndexMap(ids);
    return (id: string) => indexMap[id] ?? -1;
};

const getSortedReservationsBySortedResources = ({
    reservations,
    sortedResources,
}: ReservationsSortedResourcesParam): Array<Reservation> => {
    const copyReservations = [...reservations];
    const resourcesIds = sortedResources.map(({ id }) => id);
    const spaceCategoryIds = sortedResources.map(({ resourceCategoryId }) => resourceCategoryId);

    const getIndexOfResourceId = createIdsIndexOf(resourcesIds);
    const getIndexOfSpaceCategory = createIdsIndexOf(spaceCategoryIds);

    copyReservations.sort((a, b) => {
        const aIndex =
            a.assignedResourceId == null
                ? getIndexOfSpaceCategory(a.requestedCategoryId)
                : getIndexOfResourceId(a.assignedResourceId);
        const bIndex =
            b.assignedResourceId == null
                ? getIndexOfSpaceCategory(b.requestedCategoryId)
                : getIndexOfResourceId(b.assignedResourceId);
        return aIndex > bIndex ? 1 : -1;
    });
    return copyReservations;
};

export const getSortedReservationsBySortedSpaces = createSelector(
    getReservationsByGroupId,
    getSortedSpaces,
    (reservations, sortedSpaces) => {
        if (isEmptyOrSizeOneOfSpacesOrReservations({ reservations, sortedResources: sortedSpaces?.resources })) {
            return reservations;
        }
        return getSortedReservationsBySortedResources({ reservations, sortedResources: sortedSpaces?.resources });
    }
);
const getSortedReservationsByIds = createSelector(
    getReservationsByIds,
    getSortedSpaces,
    (reservations, sortedSpaces) => {
        if (isEmptyOrSizeOneOfSpacesOrReservations({ reservations, sortedResources: sortedSpaces?.resources })) {
            return reservations;
        }
        return getSortedReservationsBySortedResources({ reservations, sortedResources: sortedSpaces?.resources });
    }
);
export const getSortedReservationIds = flow(
    getSortedReservationsByIds,
    array.map((r) => r.id)
);

const withEnterpriseIdAndGroupId = createSelector(
    getStringProp('groupId'),
    getCurrentEnterpriseId,
    (groupId, enterpriseId) => ({
        enterpriseId,
        groupId,
    })
);
const withEnterpriseIdAndReservationId = createSelector(
    getStringProp('id'),
    getCurrentEnterpriseId,
    (id, enterpriseId) => ({
        enterpriseId,
        id,
    })
);
const withEnterpriseIdAndIds = createSelector(
    getStringArrayProp('ids'),
    getCurrentEnterpriseId,
    (ids, enterpriseId) => ({
        enterpriseId,
        ids,
    })
);
const withEnterpriseIdAndAvailabilityBlockId = createSelector(
    getStringProp('availabilityBlockId'),
    getCurrentEnterpriseId,
    (availabilityBlockId, enterpriseId) => ({ enterpriseId, availabilityBlockId })
);

export const getCurrentEnterpriseReservations = composeProps(
    getReservations,
    createSelector(getCurrentEnterpriseId, (enterpriseId) => ({ enterpriseId }))
);
export const getCurrentEnterpriseReservationsByIds = composeProps(getReservationsByIds, withEnterpriseIdAndIds);
export const getCurrentEnterpriseReservationsCustomerIds = composeProps(
    getReservationsCustomerIds,
    withEnterpriseIdAndIds
);
export const getCurrentEnterpriseReservationsByAvailabiltiyBlockId = composeProps(
    getReservationsByAvailabilityBlockId,
    withEnterpriseIdAndAvailabilityBlockId
);
export const getCurrentEnterpriseReservationGroupIdsByAvailabilityBlockId = composeProps(
    getReservationGroupIdsByAvailabilityBlockId,
    withEnterpriseIdAndAvailabilityBlockId
);
const getCurrentEnterpriseGroupLockableReservations = composeProps(
    getGroupLockableReservations,
    withEnterpriseIdAndGroupId
);
export const getCurrentEnterpriseGroupLockableReservationsIds = flow(
    getCurrentEnterpriseGroupLockableReservations,
    array.map((reservation) => reservation.id)
);
export const getCurrentEnterpriseReservationsByGroupId = composeProps(
    getReservationsByGroupId,
    withEnterpriseIdAndGroupId
);
export const getCurrentEnterpriseSortedReservationsByGroupId = composeProps(
    getSortedReservationsBySortedSpaces,
    withEnterpriseIdAndGroupId
);
export const getCurrentEnterpriseReservation = composeProps(getReservation, withEnterpriseIdAndReservationId);

export const getCurrentEnterpriseReservationResourceLocked = flow(
    getCurrentEnterpriseReservation,
    (r) => r?.assignedResourceLocked
);
export const getCurrentEnterpriseReservationAssignedResourceId = flow(
    getCurrentEnterpriseReservation,
    (r) => r?.assignedResourceId
);

export const getCurrentEnterpriseReservationsAssignedResourceIds = flow(
    getCurrentEnterpriseReservationsByIds,
    array.map((r) => r.assignedResourceId),
    compactArray,
    array.uniq(string.Eq)
);

export const getCurrentEnterpriseReservationTimeState = flow(
    getCurrentEnterpriseReservation,
    (r) => r?.reservationTimeState
);

export const getCurrentEnterpriseReservationIdsByGroupId = createSelector(
    getCurrentEnterpriseReservationsByGroupId,
    array.map((reservation) => reservation.id)
);

export const getCurrentEnterpriseReservationCustomerId = flow(getCurrentEnterpriseReservation, (r) => r?.customerId);
export const getCurrentEnterpriseReservationOwnerId = flow(getCurrentEnterpriseReservation, (r) => r?.accountId);
export const getCurrentEnterpriseReservationBookerId = flow(getCurrentEnterpriseReservation, (r) => r?.bookerId);
export const getCurrentEnterpriseReservationServiceId = flow(getCurrentEnterpriseReservation, (r) => r?.serviceId);
export const getCurrentEnterpriseReservationGroupId = flow(getCurrentEnterpriseReservation, (r) => r?.groupId);

export const getCurrentEnterpriseReservationRateId = flow(getCurrentEnterpriseReservation, (r) => r?.rateId);
export const getCurrentEnterpriseReservationsServiceIds = flow(
    getCurrentEnterpriseReservationsByIds,
    flow(
        array.map((reservation) => reservation.serviceId),
        array.uniq(string.Eq)
    )
);
export const getCurrentEnterpriseReservationPersonCount = flow(getCurrentEnterpriseReservation, (reservation) =>
    reservation ? getReservationPersonCount(reservation) : 0
);

export const isAnyCurrentEnterpriseReservationCancelled = flow(
    getCurrentEnterpriseReservationsByIds,
    array.some((reservation) => isCanceledState(reservation.serviceOrderState))
);

export const getCurrentEnterpriseReservationOccupancyData = flow(
    getCurrentEnterpriseReservation,
    (r) => r?.occupancyData
);

export const haveCurrentEnterpriseReservationsCommonBooker = composeProps(
    haveReservationsCommonBooker,
    withEnterpriseIdAndIds
);

export const getEditableHistoryWindowMilisecondCount = createSelector(
    getCurrentEnterpriseOperationalEditableHistoryWindow,
    getDurationAsMilliseconds
);

const getCurrentAccommodationEnvironment = composeProps(
    getAccommodationEnvironment,
    createPropsExtender('code', getCurrentEnterpriseAccommodationEnvironmentCode)
);

export const getCurrentCompanionAccommodationExtent = compose(
    get(['reservations', 'companionExtent']),
    getCurrentAccommodationEnvironment
);

export const selectReservationsLatestUndoCancelationUtc = createSelector(
    getCurrentEnterpriseReservationsByIds,
    getCurrentEnterpriseEditableHistoryIntervalMilliseconds,
    getServices,
    getEnterpriseTimeZoneIdentifier,
    (reservations: Reservation[], ehwMilliseconds: number, services, timezone): string | null => {
        let min: string | null = null;

        reservations.forEach((reservation) => {
            if (reservation.canceledUtc) {
                const latestUndoCancellationUtc = getReservationLatestUndoCancelationUtc(
                    reservation,
                    timezone,
                    services[reservation.serviceId],
                    ehwMilliseconds
                );
                if (latestUndoCancellationUtc && (!min || latestUndoCancellationUtc < min)) {
                    min = latestUndoCancellationUtc;
                }
            }
        });

        return min;
    }
);
